const ASSETS = new URL('./', import.meta.url);
let instanceCount = 0;

/** Original artwork clipped into independently animated SVG groups. No font substitution. */
class SpookySalesHero extends HTMLElement {
  static observedAttributes = ['layout', 'reduced-motion'];

  constructor() {
    super();
    this.attachShadow({mode:'open'});
    this.animations = [];
    this.position = 0;
    this.playing = false;
    this.userPaused = false;
    this.ready = false;
    this.inView = true;
    this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
    this.preferenceHandler = () => this.updateMotion();
    this.visibilityHandler = () => this.updateVisibility();
  }

  connectedCallback() {
    if (this.shadowRoot.childElementCount) return;
    syncSpookyViewport(this);
    const id = `spooky-${++instanceCount}`;
    const photo = this.getAttribute('image-url') || new URL('spooky-sales-model.webp',ASSETS).href;
    const title = this.getAttribute('letters-url') || new URL('spooky-sales-letters.png',ASSETS).href;
    const offer = this.getAttribute('offer-url') || new URL('spooky-sales-offer.png',ASSETS).href;
    this.setAttribute('data-loading','true');
    // SVG <image> uses the supplied PNGs unchanged. The clip windows preserve all glyph shapes.
    const slices = (source, rectangles, kind) => rectangles.map(([x,y,w,h], i) => `
      <clipPath id="${id}-${kind}-${i}"><rect x="${x}" y="${y}" width="${w}" height="${h}"/></clipPath>
      ${kind==='sales-letter' ? `<g class="sales-drift" data-index="${i}">` : ''}
      <g class="${kind}" data-index="${i}" style="transform-box:fill-box;transform-origin:center">
        <use href="#${id}-${source}" clip-path="url(#${id}-${kind}-${i})"/>
      </g>${kind==='sales-letter' ? '</g>' : ''}`).join('');
    const drips = [[110,128,37,7],[175,118,49,6],[287,119,40,7],[397,119,48,6],[478,118,38,6],[546,141,27,5],[590,118,43,6]].map(([x,y,length,width],i)=>`
      <g transform="translate(${x} ${y})">
        <g class="drip-profile" data-index="${i}">
        <g class="drip-stem" data-index="${i}" style="transform-box:fill-box;transform-origin:50% 0%">
          <path d="M ${-width} -4 C ${-width} 5 -3.5 7 -3.5 ${length*.32} C -2 ${length*.48} -4.5 ${length*.56} -3.5 ${length*.74} L -3.5 ${length-7} C -3.5 ${length-3} -7 ${length-2} -7 ${length+2} C -7 ${length+11} 7 ${length+11} 7 ${length+2} C 7 ${length-2} 3.5 ${length-3} 3.5 ${length-7} L 3.5 ${length*.74} C 4.5 ${length*.56} 2 ${length*.48} 3.5 ${length*.32} C 3.5 7 ${width} 5 ${width} -4 Z" fill="url(#${id}-slime)"/>
          <path d="M -1 3 C -1 ${length*.35} 1 ${length*.52} 0 ${length-3}" fill="none" stroke="#efffd7" stroke-width="1.4" opacity=".7"/>
        </g>
        </g>
      </g>`).join('');
    // Extra mobile drops align with the gaps around SALES after enlarging SPOOKY.
    const drops = [[175,176],[289,169],[397,175],[490,171],[612,168],[110,180],[478,178],[590,177]].map(([x,y],i)=>`
      <g class="drop-profile ${i>=5 ? 'mobile-extra' : ''}" data-index="${i}" transform="translate(${x} ${y})"><ellipse class="falling-drop" data-index="${i}" cx="0" cy="0" rx="${i>=5 ? 4.2 : 3.2}" ry="${i>=5 ? 6 : 4.8}" fill="url(#${id}-slime)" opacity="0" style="transform-box:fill-box;transform-origin:center"/></g>`).join('');
    this.shadowRoot.innerHTML = `
      <style>
        :host{--word-scale:1;--word-lift:0px;--drip-width:1;--drip-length:1;--mobile-drops:none;display:block;position:relative;isolation:isolate;background:#25070e;color:white;overflow:hidden;aspect-ratio:2471/1080}
        *,*:before,*:after{box-sizing:border-box}.photo{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:50% 50%;display:block}
        .art{position:absolute;left:auto;right:7%;top:50%;width:32%;max-width:620px;transform:translateY(-50%);display:flex;align-items:center;flex-direction:column;gap:18px}
        svg{display:block;overflow:visible}.brand{width:82%;height:auto;aspect-ratio:600/280}.offer{width:100%;height:auto;aspect-ratio:630/396}.ambient{transform-box:fill-box;transform-origin:center}
        .ectoplasm{filter:drop-shadow(0 0 4px #85ff40) drop-shadow(0 0 9px #9fff4180)}
        .word-mobile-scale{transform-box:view-box;transform-origin:50% 29%;transform:translateY(var(--word-lift)) scale(var(--word-scale))}
        .drip-profile{transform-box:fill-box;transform-origin:50% 0%;transform:scale(var(--drip-width),var(--drip-length))}.mobile-extra{display:var(--mobile-drops)}
        :host([data-loading]) .art{opacity:0}
        :host([data-motion=reduced]) .ectoplasm,:host([data-motion=reduced]) .halo,:host([data-motion=reduced]) .spectral-light,:host([data-motion=reduced]) .after-entry{display:none}
        .discount-ambient{transform-box:fill-box;transform-origin:center}.band-ambient{transform-box:fill-box;transform-origin:center}
        .sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
        :host([layout=portrait]){--word-scale:1.26;--word-lift:-24px;--drip-width:1.22;--drip-length:1.18;--mobile-drops:inline;aspect-ratio:708/1155}:host([layout=portrait]) .photo{object-position:47% 50%}:host([layout=portrait]) .art{left:15%;right:auto;top:auto;bottom:12%;width:70%;max-width:none;transform:none;gap:12px}
        :host([layout=portrait]) .brand{width:78%}:host([layout=portrait]) .offer{width:89%}
        :host([layout=portrait]) .drip-profile[data-index="1"]{--drip-length:.92}:host([layout=portrait]) .drop-profile[data-index="0"],:host([layout=portrait]) .drop-profile[data-index="1"]{display:none}
        @media(max-width:760px){:host(:not([layout=landscape])){--word-scale:1.26;--word-lift:-24px;--drip-width:1.22;--drip-length:1.18;--mobile-drops:inline;aspect-ratio:708/1155}:host(:not([layout=landscape])) .photo{object-position:47% 50%}:host(:not([layout=landscape])) .art{left:15%;right:auto;top:auto;bottom:12%;width:70%;max-width:none;transform:none;gap:12px}:host(:not([layout=landscape])) .brand{width:78%}:host(:not([layout=landscape])) .offer{width:89%}:host(:not([layout=landscape])) .drip-profile[data-index="1"]{--drip-length:.92}:host(:not([layout=landscape])) .drop-profile[data-index="0"],:host(:not([layout=landscape])) .drop-profile[data-index="1"]{display:none}}
      
        :host([viewport-fit]){height:var(--spooky-hero-height);aspect-ratio:auto;container-type:size}
        :host([viewport-fit]) .photo{object-position:50% 0%}
        :host([viewport-fit]) .art{left:77%;right:auto;top:auto;bottom:calc(3.5% + 74px);width:min(32%,calc((100cqh - 132px)*.9));transform:translateX(-50%)}
        @media(max-width:760px){:host([viewport-fit]) .photo{object-position:47% 0%}:host([viewport-fit]) .art{left:50%;right:auto;bottom:max(12%,80px);width:min(70%,calc((100cqh - 148px)*1.05));transform:translateX(-50%);max-width:none;gap:12px}}
      
        .brand-frame{position:relative;width:82%;aspect-ratio:600/280}
        .offer-frame{position:relative;width:100%;aspect-ratio:630/396}
        .brand-frame .brand,.offer-frame .offer{position:relative;z-index:1;width:100%!important;height:100%;pointer-events:none}
        .entry-overlay,.entry-word,.entry-word-ambient,.entry-sales-drift,.entry-hasta-ambient,.entry-discount-ambient{position:absolute;inset:0;pointer-events:none}
        .entry-overlay{z-index:0}
        .entry-word-ambient{transform-origin:300px 146px}
        .entry-discount-ambient{transform-origin:315px 205px}
        .entry-canvas{position:absolute;left:0;top:0;width:600px;height:280px;transform-origin:0 0;transform:scale(var(--entry-title-scale,1))}
        .entry-offer-canvas{width:630px;height:396px;transform:scale(var(--entry-offer-scale,1))}
        .entry-word{transform-origin:50% 29%;transform:translateY(var(--word-lift)) scale(var(--word-scale))}
        .entry-tile{position:absolute;will-change:transform,opacity}
        .entry-cut{position:absolute;inset:0;overflow:hidden}
        .entry-image{position:absolute;display:block;max-width:none}
        .entry-mobile-ink{display:none;position:absolute;inset:0;pointer-events:none;-webkit-mask-image:var(--entry-ink-source);mask-image:var(--entry-ink-source);mask-mode:alpha;-webkit-mask-size:700px var(--entry-ink-height);mask-size:700px var(--entry-ink-height);-webkit-mask-position:var(--entry-ink-x) var(--entry-ink-y);mask-position:var(--entry-ink-x) var(--entry-ink-y);-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat}
        .entry-letter .entry-mobile-ink{background:linear-gradient(100deg,#baff84,#dcffb0 48%,#baff84);opacity:.7}
        .entry-digit .entry-mobile-ink{background:#ff2245;opacity:0}
        :host([layout=portrait]) .entry-mobile-ink{display:block}
        :host([layout=portrait]) .entry-letter{filter:drop-shadow(0 0 4px #9fff4670) drop-shadow(0 0 8px #9fff4745)}
        :host([layout=portrait]) .mobile-halo-gain{opacity:.45}
        @media(max-width:760px){:host(:not([layout=landscape])) .entry-mobile-ink{display:block}:host(:not([layout=landscape])) .entry-letter{filter:drop-shadow(0 0 4px #9fff4670) drop-shadow(0 0 8px #9fff4745)}:host(:not([layout=landscape])) .mobile-halo-gain{opacity:.45}}
        /* SVG carries decoration only. Never exchange or hide the readable HTML
           lettering when the entrance ends, including on iPhone/WebKit. */
        .brand .letter,.brand .sales-letter,.offer .hasta,.offer .digit,.offer .strip,.offer .terms{display:none}
        :host([layout=portrait]) .brand-frame{width:78%}
        :host([layout=portrait]) .offer-frame{width:89%}
        @media(max-width:760px){:host(:not([layout=landscape])) .brand-frame{width:78%}:host(:not([layout=landscape])) .offer-frame{width:89%}}
      </style>
      <img class="photo" width="2471" height="1080" alt="Modelo diëtz sentado, con prenda rosa, sobre fondo burdeos" fetchpriority="high" decoding="async">
      <h1 class="sr-only">Spooky Sales. Hasta 35% OFF en toda la tienda.</h1>
      <p class="sr-only">Aplican términos y condiciones.</p>
      <div class="art" aria-hidden="true">
        <div class="brand-frame"><svg class="brand" viewBox="50 0 600 280" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <image id="${id}-title" width="700" height="292"/>
            <clipPath id="${id}-word-window"><rect x="0" y="0" width="700" height="166"/></clipPath>
            <clipPath id="${id}-sales-window"><rect x="0" y="166" width="700" height="126"/></clipPath>
            <mask id="${id}-word-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="700" height="166" style="mask-type:alpha"><use href="#${id}-title"/></mask>
            <mask id="${id}-sales-mask" maskUnits="userSpaceOnUse" x="0" y="166" width="700" height="126" style="mask-type:alpha"><use href="#${id}-title"/></mask>
            <linearGradient id="${id}-shine"><stop offset="0" stop-color="#b8ff99" stop-opacity="0"/><stop offset=".35" stop-color="#b8ff99" stop-opacity=".12"/><stop offset=".5" stop-color="#f4ffe9" stop-opacity=".8"/><stop offset=".65" stop-color="#b8ff99" stop-opacity=".12"/><stop offset="1" stop-color="#b8ff99" stop-opacity="0"/></linearGradient>
            <linearGradient id="${id}-ghost-shine"><stop offset="0" stop-color="#c6c0ff" stop-opacity="0"/><stop offset=".5" stop-color="#b9adff" stop-opacity=".9"/><stop offset="1" stop-color="#c6c0ff" stop-opacity="0"/></linearGradient>
            <linearGradient id="${id}-slime" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#87fb40"/><stop offset=".5" stop-color="#e8ffc5"/><stop offset="1" stop-color="#9bff51"/></linearGradient>
            <filter id="${id}-neon" x="-25%" y="-35%" width="150%" height="180%" color-interpolation-filters="sRGB"><feColorMatrix type="matrix" values="0 0 0 0 .43 0 0 0 0 1 0 0 0 0 .12 0 0 0 1 0"/><feGaussianBlur stdDeviation="8"/></filter>
            <filter id="${id}-ghost-ink" x="-15%" y="-20%" width="130%" height="140%" color-interpolation-filters="sRGB"><feColorMatrix type="matrix" values="0 0 0 0 .68 0 0 0 0 .6 0 0 0 0 1 0 0 0 1 0"/><feGaussianBlur stdDeviation="1.2"/></filter>
          </defs>
          <g class="word-mobile-scale">
            <g class="ghost" clip-path="url(#${id}-word-window)" opacity="0" style="filter:blur(7px);transform-box:fill-box;transform-origin:center"><use href="#${id}-title"/></g>
            <g class="ambient word">
            <g class="mobile-halo-gain"><g class="halo" clip-path="url(#${id}-word-window)" filter="url(#${id}-neon)" opacity="0"><use href="#${id}-title"/></g></g>
            <g class="ectoplasm" opacity="0">${drips}${drops}</g>
            ${slices('title',[[0,0,153,166],[153,0,83,166],[236,0,107,166],[343,0,106,166],[449,0,90,166],[539,0,161,166]],'letter')}
            <g mask="url(#${id}-word-mask)"><rect class="spectral-light" x="-350" y="0" width="350" height="166" fill="url(#${id}-shine)" style="mix-blend-mode:screen"/></g>
            </g>
          </g>
          <g class="after-entry" opacity="0"><g class="sales-echo" filter="url(#${id}-ghost-ink)"><g clip-path="url(#${id}-sales-window)"><use href="#${id}-title"/></g></g></g>
          <g class="sales-row">${slices('title',[[98,166,70,126],[200,166,86,126],[320,166,66,126],[422,166,66,126],[524,166,73,126]],'sales-letter')}</g>
          <g class="after-entry" opacity="0" mask="url(#${id}-sales-mask)"><rect class="sales-light" x="-260" y="170" width="260" height="110" fill="url(#${id}-ghost-shine)"/></g>
        </svg>
          <div class="entry-overlay"><div class="entry-canvas">
            <div class="entry-word"><div class="entry-word-ambient">${spookyEntryTiles('title',[50,0,600,280],[[0,0,153,166],[153,0,83,166],[236,0,107,166],[343,0,106,166],[449,0,90,166],[539,0,161,166]],'letter')}</div></div>
            ${spookyEntryTiles('title',[50,0,600,280],[[98,166,70,126],[200,166,86,126],[320,166,66,126],[422,166,66,126],[524,166,73,126]],'sales-letter')}
          </div></div>
        </div>
        <div class="offer-frame"><svg class="offer" viewBox="35 35 630 396" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <image id="${id}-offer" width="700" height="480"/>
            <clipPath id="${id}-numbers-window"><rect x="0" y="84" width="700" height="238"/></clipPath>
            <mask id="${id}-band-mask" maskUnits="userSpaceOnUse" x="0" y="322" width="700" height="83" style="mask-type:luminance"><use href="#${id}-offer"/></mask>
            <linearGradient id="${id}-band-shine"><stop offset="0" stop-color="#f1ffd8" stop-opacity="0"/><stop offset=".5" stop-color="#ffffff" stop-opacity="1"/><stop offset="1" stop-color="#f1ffd8" stop-opacity="0"/></linearGradient>
            <filter id="${id}-blood-aura" x="-15%" y="-15%" width="130%" height="130%" color-interpolation-filters="sRGB"><feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 .09 0 0 0 0 .2 0 0 0 1 0"/><feGaussianBlur stdDeviation="6"/></filter>
          </defs>
          <g class="hasta-ambient">${slices('offer',[[0,0,700,94]],'hasta')}</g>
          <g class="discount-ambient">
            <g class="after-entry" opacity="0"><g class="discount-aura" filter="url(#${id}-blood-aura)"><g clip-path="url(#${id}-numbers-window)"><use href="#${id}-offer"/></g></g></g>
            <g class="offer-numbers">
              ${slices('offer',[[0,94,128,228],[128,94,88,228],[216,94,218,228],[434,94,91,228],[525,94,70,228],[595,94,105,228]],'digit')}
            </g>
          </g>
          <g class="band-ambient">
            <g class="after-entry" opacity="0"><rect class="band-aura" x="39" y="331" width="621" height="71" fill="#b8ff66" style="filter:blur(12px)"/></g>
            ${slices('offer',[[0,322,700,83]],'strip')}
            <g class="after-entry" opacity="0" mask="url(#${id}-band-mask)"><rect class="band-light" x="-240" y="322" width="240" height="83" fill="url(#${id}-band-shine)"/></g>
          </g>
          ${slices('offer',[[0,405,700,75]],'terms')}
        </svg>
          <div class="entry-overlay"><div class="entry-canvas entry-offer-canvas">
            <div class="entry-hasta-ambient">${spookyEntryTiles('offer',[35,35,630,396],[[0,0,700,94]],'hasta')}</div>
            <div class="entry-discount-ambient">${spookyEntryTiles('offer',[35,35,630,396],[[0,94,128,228],[128,94,88,228],[216,94,218,228],[434,94,91,228],[525,94,70,228],[595,94,105,228]],'digit')}</div>
            ${spookyEntryTiles('offer',[35,35,630,396],[[0,322,700,83]],'strip')}
            ${spookyEntryTiles('offer',[35,35,630,396],[[0,405,700,75]],'terms')}
          </div></div>
        </div>
      </div>`;
    this.shadowRoot.querySelector('.photo').src = photo;
    syncSpookyEntryScale(this);
    this.shadowRoot.querySelectorAll('.entry-image').forEach(image=>{
      image.src=image.dataset.source==='title' ? title : offer;
      const ink=image.parentElement.querySelector('.entry-mobile-ink');
      if(ink) ink.style.setProperty('--entry-ink-source',`url(${JSON.stringify(image.src)})`);
    });
    if(this.hasAttribute('heading')) this.shadowRoot.querySelector('h1').textContent=this.getAttribute('heading');
    if(this.hasAttribute('terms')) this.shadowRoot.querySelector('.sr-only p, p.sr-only').textContent=this.getAttribute('terms');
    if(this.hasAttribute('model-alt')) this.shadowRoot.querySelector('.photo').alt=this.getAttribute('model-alt');
    this.shadowRoot.querySelector(`#${id}-title`).setAttribute('href',title);
    this.shadowRoot.querySelector(`#${id}-offer`).setAttribute('href',offer);
    this.motionPreference.addEventListener('change',this.preferenceHandler);
    document.addEventListener('visibilitychange',this.visibilityHandler);
    this.observer = new IntersectionObserver(entries => {
      this.inView = entries[0].isIntersecting;
      this.updateVisibility();
    },{threshold:0.05});
    this.observer.observe(this);
    // Avoid revealing the lettering before its source graphics are decoded.
    const sources=[photo,title,offer].map(src=>{
      const image=new Image(); image.fetchPriority='high'; image.src=src;
      return image.decode();
    });
    Promise.all(sources).then(async()=>{
      if(!this.isConnected) return;
      await waitForSpookyStorefront(this);
      if(!this.isConnected) return;
      this.ready=true;
      this.createAnimations();
      this.removeAttribute('data-loading');
      this.updateMotion();
      this.dispatchEvent(new Event('motion-ready'));
    }).catch(()=>{
      this.setAttribute('data-asset-error','true');
      this.removeAttribute('data-loading');
      this.dispatchEvent(new Event('motion-error'));
    });
  }

  disconnectedCallback() {
    this.animations.forEach(a=>a.cancel());
    this.observer?.disconnect();
    this.headerResizeObserver?.disconnect();
    this.entryResizeObserver?.disconnect();
    this.motionPreference.removeEventListener('change',this.preferenceHandler);
    document.removeEventListener('visibilitychange',this.visibilityHandler);
  }

  attributeChangedCallback(name) {
    if(name==='reduced-motion' && this.ready) this.updateMotion();
  }

  get reduced() {return this.hasAttribute('reduced-motion') || this.motionPreference.matches;}
  get currentTime() {return this.playing ? this.position+performance.now()-this.startedAt : this.position;}

  add(target, frames, options) {
    const a=target.animate(frames,{fill:'both',iterations:1,easing:'cubic-bezier(.16,1,.3,1)',...options});
    a.pause(); a.currentTime=this.position;
    this.animations.push(a);
    // Mirror the existing ambient timeline onto persistent HTML lettering.
    // SVG keeps the drips/glints; HTML keeps the original pixels readable.
    const selector=target.matches('.word') ? '.entry-word-ambient'
      : target.matches('.sales-drift') ? `.entry-sales-drift[data-index="${target.dataset.index}"]`
      : target.matches('.hasta-ambient') ? '.entry-hasta-ambient'
      : target.matches('.discount-ambient') ? '.entry-discount-ambient'
      : null;
    if(selector) this.add(this.shadowRoot.querySelector(selector),frames,options);
  }

  createAnimations() {
    this.animations.forEach(a=>a.cancel()); this.animations=[];
    const root=this.shadowRoot;
    root.querySelectorAll('.entry-letter').forEach((el,i)=>this.add(el,[
      {opacity:0,transform:`translateY(${i%2 ? 34 : 48}px) scale(.93)`},
      {opacity:1,transform:'translateY(-3px) scale(1.008)',offset:.72},
      {opacity:1,transform:'translateY(0) scale(1)'}
    ],{duration:1150,delay:150+i*90}));
    this.add(root.querySelector('.ghost'),[
      {opacity:0,transform:'translateY(20px) scale(.98)'},
      {opacity:.55,transform:'translateY(4px) scale(1.04)',offset:.5},
      {opacity:0,transform:'translateY(-12px) scale(1.1)'}
    ],{duration:1900,delay:100,easing:'ease-in-out'});
    root.querySelectorAll('.entry-sales-letter').forEach((el,i)=>this.add(el,[
      {opacity:0,transform:`translate(${(i-2)*12}px, 9px)`},
      {opacity:1,transform:'translate(0,0)'}
    ],{duration:1050,delay:550+i*70}));
    this.add(root.querySelector('.entry-hasta'),[{opacity:0,transform:'translateY(8px)'},{opacity:1,transform:'translateY(0)'}],{duration:800,delay:900});
    root.querySelectorAll('.entry-digit').forEach((el,i)=>this.add(el,[
      {opacity:0,transform:'translateY(28px) scale(.93)'},
      {opacity:1,transform:'translateY(-2px) scale(1.015)',offset:.75},
      {opacity:1,transform:'translateY(0) scale(1)'}
    ],{duration:900,delay:1030+i*55}));
    this.add(root.querySelector('.entry-strip'),[
      {opacity:0,clipPath:'inset(0 50% 0 50%)',transform:'translateY(5px)'},
      {opacity:1,clipPath:'inset(0 0% 0 0%)',transform:'translateY(0)'}
    ],{duration:850,delay:1530});
    this.add(root.querySelector('.entry-terms'),[{opacity:0},{opacity:1}],{duration:600,delay:1990});
    this.add(root.querySelector('.ectoplasm'),[{opacity:0},{opacity:.95}],{duration:1100,delay:1450});
    this.add(root.querySelector('.halo'),[
      {opacity:.25,transform:'scale(1)'},
      {opacity:.95,transform:'scale(1.015)',offset:.5},
      {opacity:.25,transform:'scale(1)'}
    ],{duration:5200,delay:1800,iterations:Infinity,easing:'ease-in-out'});
    root.querySelectorAll('.drip-stem').forEach((el,i)=>this.add(el,[
      {transform:'scaleY(.4)'},
      {transform:'scaleY(.72)',offset:.34},
      {transform:'scaleY(1.05)',offset:.69},
      {transform:'scaleY(.93)',offset:.79},
      {transform:'scaleY(.4)'}
    ],{duration:4300+i*290,delay:2100+i*180,iterations:Infinity,easing:'ease-in-out'}));
    root.querySelectorAll('.falling-drop').forEach((el,i)=>this.add(el,[
      {opacity:0,transform:'translateY(0) scale(.7)'},
      {opacity:0,transform:'translateY(0) scale(.7)',offset:.5},
      {opacity:.95,transform:'translateY(0) scale(1)',offset:.55},
      {opacity:.8,transform:'translateY(30px) scale(.78,1.2)',offset:.7},
      {opacity:0,transform:'translateY(47px) scale(.5,.8)',offset:.78},
      {opacity:0,transform:'translateY(47px) scale(.5,.8)'}
    ],{duration:i>=5 ? 4200+(i-5)*350 : 5400+i*320,delay:i>=5 ? 2700+(i-5)*650 : 2500+i*510,iterations:Infinity,easing:'linear'}));
    // Slow ambient loops do not reset the legibility of the promotion.
    this.add(root.querySelector('.word'),[
      {transform:'translateY(0)',filter:'brightness(1)'},
      {transform:'translateY(-4px)',filter:'brightness(1.2)',offset:.5},
      {transform:'translateY(0)',filter:'brightness(1)'}
    ],{duration:6800,delay:2600,iterations:Infinity,easing:'ease-in-out'});
    this.add(root.querySelector('.spectral-light'),[
      {transform:'translateX(0)',opacity:0},
      {transform:'translateX(0)',opacity:0,offset:.12},
      {transform:'translateX(160px)',opacity:.8,offset:.24},
      {transform:'translateX(870px)',opacity:.8,offset:.68},
      {transform:'translateX(1050px)',opacity:0,offset:.82},
      {transform:'translateX(1050px)',opacity:0}
    ],{duration:8200,delay:2600,iterations:Infinity,easing:'linear'});

    // The rest of the composition becomes haunted only after the one-off entrance.
    // Originals remain fully readable; effects live on separate parent/overlay layers.
    root.querySelectorAll('.after-entry').forEach(el=>this.add(el,[{opacity:0},{opacity:1}],{duration:800,delay:2600}));
    root.querySelectorAll('.sales-drift').forEach((el,i)=>this.add(el,[
      {transform:'translateY(0)'},
      {transform:`translateY(${i%2 ? 2 : -3}px)`,offset:.5},
      {transform:'translateY(0)'}
    ],{duration:5900+i*370,delay:2900+i*240,iterations:Infinity,easing:'ease-in-out'}));
    this.add(root.querySelector('.sales-echo'),[
      {opacity:.08,transform:'translate(-1px,1px)'},
      {opacity:.5,transform:'translate(3px,3px)',offset:.5},
      {opacity:.08,transform:'translate(-1px,1px)'}
    ],{duration:7100,delay:3000,iterations:Infinity,easing:'ease-in-out'});
    this.add(root.querySelector('.sales-light'),[
      {transform:'translateX(0)'},
      {transform:'translateX(0)',offset:.15},
      {transform:'translateX(1000px)',offset:.82},
      {transform:'translateX(1000px)'}
    ],{duration:9600,delay:3700,iterations:Infinity,easing:'linear'});
    this.add(root.querySelector('.hasta-ambient'),[
      {opacity:1,transform:'translateY(0)'},
      {opacity:.84,transform:'translateY(-1.5px)',offset:.5},
      {opacity:1,transform:'translateY(0)'}
    ],{duration:6300,delay:3000,iterations:Infinity,easing:'ease-in-out'});
    const heartbeat=[
      {transform:'translateY(0) scale(1)'},
      {transform:'translateY(0) scale(1)',offset:.16},
      {transform:'translateY(-1px) scale(1.012)',offset:.28},
      {transform:'translateY(0) scale(1)',offset:.4},
      {transform:'translateY(-.5px) scale(1.007)',offset:.51},
      {transform:'translateY(0) scale(1)',offset:.64},
      {transform:'translateY(0) scale(1)'}
    ];
    this.add(root.querySelector('.discount-ambient'),heartbeat,{duration:7800,delay:3200,iterations:Infinity,easing:'ease-in-out'});
    this.add(root.querySelector('.discount-aura'),[
      {opacity:.06},{opacity:.06,offset:.16},{opacity:.7,offset:.28},
      {opacity:.12,offset:.4},{opacity:.42,offset:.51},{opacity:.06,offset:.64},{opacity:.06}
    ],{duration:7800,delay:3200,iterations:Infinity,easing:'ease-in-out'});
    root.querySelectorAll('.entry-digit .entry-mobile-ink').forEach(el=>this.add(el,[
      {opacity:0},{opacity:0,offset:.16},{opacity:1,offset:.28},
      {opacity:.2,offset:.4},{opacity:.85,offset:.51},{opacity:0,offset:.64},{opacity:0}
    ],{duration:7800,delay:3200,iterations:Infinity,easing:'ease-in-out'}));
    this.add(root.querySelector('.band-aura'),[
      {opacity:.28},{opacity:.9,offset:.5},{opacity:.28}
    ],{duration:6000,delay:3500,iterations:Infinity,easing:'ease-in-out'});
    this.add(root.querySelector('.band-light'),[
      {transform:'translateX(0)'},
      {transform:'translateX(0)',offset:.18},
      {transform:'translateX(1000px)',offset:.82},
      {transform:'translateX(1000px)'}
    ],{duration:7200,delay:4100,iterations:Infinity,easing:'linear'});
  }

  play() {
    if(!this.ready || this.reduced) return;
    this.userPaused=false;
    if(!this.inView || document.hidden) return;
    this.resume();
  }

  resume() {
    if(this.playing) return;
    const start=document.timeline.currentTime-this.position;
    this.animations.forEach(a=>{a.currentTime=this.position; a.play(); a.startTime=start;});
    this.startedAt=performance.now(); this.playing=true;
    this.emitState();
  }

  pause(user=true) {
    if(user) this.userPaused=true;
    this.position=this.currentTime;
    this.playing=false;
    this.animations.forEach(a=>{a.pause();a.currentTime=this.position;});
    this.emitState();
  }

  seek(ms) {
    this.pause(); this.position=Math.max(0,ms);
    this.animations.forEach(a=>{a.currentTime=this.position;});
    this.emitState();
  }

  replay() {
    if(!this.ready || this.reduced) return;
    this.pause(false);this.position=0;
    this.animations.forEach(a=>{a.currentTime=0;});
    this.play();
  }

  updateMotion() {
    if(!this.ready) return;
    if(this.reduced) {
      this.pause(false);this.position=2600;
      this.animations.forEach(a=>{a.currentTime=2600;});
      this.setAttribute('data-motion','reduced');
    } else {
      this.setAttribute('data-motion','animated');
      if(!this.userPaused) this.play();
    }
    this.emitState();
  }

  updateVisibility() {
    if(!this.ready || this.reduced) return;
    if(document.hidden || !this.inView) this.pause(false);
    else if(!this.userPaused) this.resume();
  }

  emitState() {
    this.setAttribute('data-playing',String(this.playing));
    this.dispatchEvent(new CustomEvent('motion-state',{detail:{playing:this.playing,reduced:this.reduced,time:this.currentTime}}));
  }
}
if(!customElements.get('spooky-sales-hero')) customElements.define('spooky-sales-hero',SpookySalesHero);

function spookyEntryTiles(source,viewBox,rectangles,kind) {
  const [vx,vy]=viewBox;
  const imageHeight=source==='title' ? 292 : 480;
  return rectangles.map(([x,y,w,h],i)=>{
    // Match SVG's full-image transform origin, including the cropped viewBox.
    const origin=`${350-x}px ${imageHeight/2-y}px`;
    const ink=kind==='letter' || kind==='digit' ? `<span class="entry-mobile-ink" style="--entry-ink-height:${imageHeight}px;--entry-ink-x:${-x}px;--entry-ink-y:${-y}px"></span>` : '';
    const tile=`<div class="entry-tile entry-${kind}" style="left:${x-vx}px;top:${y-vy}px;width:${w}px;height:${h}px;transform-origin:${origin}">
      <div class="entry-cut"><img class="entry-image" data-source="${source}" width="700" height="${imageHeight}" alt="" decoding="async" style="width:700px;height:${imageHeight}px;left:${-x}px;top:${-y}px">${ink}</div>
    </div>`;
    return kind==='sales-letter' ? `<div class="entry-sales-drift" data-index="${i}">${tile}</div>` : tile;
  }).join('');
}

function syncSpookyEntryScale(hero) {
  const brand=hero.shadowRoot.querySelector('.brand-frame');
  const offer=hero.shadowRoot.querySelector('.offer-frame');
  const measure=()=>{
    brand.style.setProperty('--entry-title-scale',String(brand.getBoundingClientRect().width/600));
    offer.style.setProperty('--entry-offer-scale',String(offer.getBoundingClientRect().width/630));
  };
  measure();
  hero.entryResizeObserver=new ResizeObserver(measure);
  hero.entryResizeObserver.observe(brand);hero.entryResizeObserver.observe(offer);
}

async function waitForSpookyStorefront(hero) {
  if(hero.reduced || document.hidden) return;
  hero.setAttribute('data-startup','waiting');
  const deadline=performance.now()+1600;
  // Module/deferred theme scripts finish at DOMContentLoaded. Do not wait for
  // below-the-fold images, trackers or an indefinitely delayed window.load.
  const navigation=performance.getEntriesByType('navigation')[0];
  if(document.readyState!=='complete' && !navigation?.domContentLoadedEventEnd) {
    await new Promise(resolve=>{
      const done=()=>{
        clearTimeout(timer);
        document.removeEventListener('DOMContentLoaded',done);
        resolve();
      };
      const timer=setTimeout(done,900);
      document.addEventListener('DOMContentLoaded',done,{once:true});
    });
  }
  if(!hero.isConnected || document.hidden) return;
  // Give header sizing, image rasterization and app initialization a short
  // quiet paint window. A deadline prevents third-party scripts delaying it forever.
  await new Promise(resolve=>{
    let previous=null,quiet=0,size='';
    const frame=now=>{
      if(!hero.isConnected || document.hidden) {resolve();return;}
      const nextSize=`${hero.clientWidth}:${hero.clientHeight}`;
      const gap=previous===null ? 0 : now-previous;
      quiet=gap<=34 && nextSize===size ? quiet+gap : 0;
      previous=now;size=nextSize;
      if(quiet>=160 || now>=deadline) {resolve();return;}
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  });
  hero.setAttribute('data-startup','ready');
}

function syncSpookyViewport(hero) {
  const section=hero.closest('.spooky-sales-section');
  const group=document.querySelector('#header-group');
  if(!section || !group) return;
  // The header group uses display:contents, so measure its visible sections.
  // Keep fractional pixels instead of the theme's rounded header-height value.
  const measure=()=>{
    const height=[...group.children].reduce((total,child)=>total+child.getBoundingClientRect().height,0);
    section.style.setProperty('--spooky-header-height',`${height}px`);
  };
  measure();
  hero.headerResizeObserver=new ResizeObserver(measure);
  [...group.children].forEach(child=>hero.headerResizeObserver.observe(child));
}
